<template>
  <div class="row">
    <div class="col-md-12 m-b-2">
      <el-table :data="shipping" :row-class-name="highlightChanges" border>
        <el-table-column prop="label"
                         label="Shipping Address"
                         class-name="bold"
                         :min-width="10"/>
        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30"/>
        <el-table-column prop="updated"
                         label="Refund"
                         header-align="center"
                         :min-width="30"/>
      </el-table>
    </div>
    <div class="col-md-12 m-b-2">
      <el-table :data="products" :row-class-name="areProductsEqual" border>
        <el-table-column prop="label"
                         label="Line Items"
                         class-name="bold"
                         :min-width="10"/>

        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {d365Item, lineItem, returnItem}}">
            <div>{{ lineItem.title }}</div>
            <div>{{ lineItem.sku }}</div>
            <div>Qty: {{ lineItem.quantity }}</div>
            <price-display
                :originalPrice="Number(lineItem.price)"
                :updatePrice="Number(d365Item.SalesPrice)"
            />
          </template>
        </el-table-column>

        <el-table-column prop="updated"
                         label="Refund"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {d365Item, lineItem, returnItem, exchange}}">
            <div>{{ lineItem.title }}</div>
            <div>{{ lineItem.sku }}</div>
            <div>Qty: {{ returnItem.quantity }}</div>
            <div v-if="exchange">Exchange</div>
            <div v-else>{{ returnItem.price | currency }}</div>
          </template>
        </el-table-column>

      </el-table>
    </div>

<!--    Payments-->
    <div class="col-md-12 m-b-2">
      <el-table :data="payment" :row-class-name="arePaymentLinesEqual" border>
        <el-table-column prop="label"
                         label="Payment Authorized"
                         class-name="bold"
                         :min-width="10"/>
        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {original}}">
            <template v-if="typeof original === 'string'">
              {{ original }}
            </template>
            <template v-else-if="typeof original === 'object'">
              <span>{{ original.price | currency }}</span> -
              <small>{{ original.rate | percent }}</small>
              <small>{{ original.title }}</small>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="refunded"
                         label="Refund"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {refunded}}">
            <template v-if="typeof refunded === 'string'">
              {{ refunded }}
            </template>
            <template v-else-if="typeof refunded === 'object'">
              <span>{{ refunded.price | currency }}</span> -
              <small>{{ refunded.rate | percent }}</small>
              <small>{{ refunded.title }}</small>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import {currency, percent} from '../../../filters';
import {extractProperty, rollUpTaxLines} from '../../../utils';
import {SHOPIFY_ADDRESS_FIELDS} from '../../../lang/en/constants';
import PriceDisplay from '../Shared/PriceDisplay';

export default {
  name: 'OrderReturnDifference',
  components: {
    PriceDisplay,
  },
  filters: {
    currency,
    percent,
  },
  computed: {
    ...mapState({
      orders: state => {
        let {orderReturn: {order, draft, draftRA, logOnOrder}} = state;
        return {
          shopify: order,
          draft: draft,
          refund: draftRA,
          log_on: logOnOrder,
        };
      },
      products: state => state.orderReturn.products,
      discountPriceRule: state => state.orderReturn.discount.price_rule,
    }),
    orderTaxLines() {
      if (!this.orders.shopify) {
        return [];
      }
      return rollUpTaxLines(this.orders.shopify.tax_lines);
    },
    refundOrderTaxLines() {
      if (!this.orders.shopify) {
        return [];
      }
      return rollUpTaxLines(this.orders.refund.tax_lines);
    },
    shipping() {
      if (!this.orders.shopify || !this.orders.refund) {
        return [
          {
            label: '',
            original: '',
            updated: '',
          }];
      }

      let {
        shopify: {shipping_address: shopifyShippingAddress},
        refund: {shipping_address: refundShippingAddress},
      } = this.orders;

      shopifyShippingAddress = shopifyShippingAddress || this.orders.shopify.billing_address;
      refundShippingAddress = refundShippingAddress || this.orders.refund.billing_address ||
          this.orders.shopify.billing_address;

      return Object.entries(SHOPIFY_ADDRESS_FIELDS).map(([key, value]) => ({
        label: value,
        original: this.extractProperty(shopifyShippingAddress, key),
        updated: this.extractProperty(refundShippingAddress, key),
      }));
    },
    payment() {
      if (!this.orders.shopify || !this.orders.refund) {
        return [];
      }

      const {shopify: originalOrder, refund: refundOrder} = this.orders;
      const taxLines = [];
      const taxLinesMax = Math.max(
          this.orderTaxLines.length,
          this.refundOrderTaxLines.length,
      );

      for (let key = 0; key < taxLinesMax; key++) {
        let original = {
          price: 0,
          rate: 0,
          title: '',
        };
        let refunded = {
          price: 0,
          rate: 0,
          title: '',
        };

        if (this.orderTaxLines[key]) {
          original = {
            price: this.orderTaxLines[key].price,
            rate: this.orderTaxLines[key].rate,
            title: this.orderTaxLines[key].title,
          };
        }

        if (this.refundOrderTaxLines[key]) {
          refunded = {
            price: this.refundOrderTaxLines[key].price,
            rate: this.refundOrderTaxLines[key].rate,
            title: this.refundOrderTaxLines[key].title,
          };
        }

        taxLines.push({
          label: `Tax Line ${key + 1}`,
          original,
          refunded,
        });
      }

      const originalShippingPrice = originalOrder.shipping_lines.length > 0
          ? currency(originalOrder.shipping_lines[0].price)
          : 0;
      const originalShippingTitle = originalOrder.shipping_lines.length > 0
          ? originalOrder.shipping_lines[0].title
          : 'No Shipping';
      //
      const refundedShippingPrice = (refundOrder && refundOrder.shipping_line)
          ? currency(refundOrder.shipping_line.price)
          : 0;
      const refundedShippingTitle = (refundOrder && refundOrder.shipping_line)
          ? refundOrder.shipping_line.title
          : 'No Shipping';

      const adjustments = originalOrder.adjustments.reduce((carry, current) => carry + parseFloat(current.amount), 0);

      return [
        {
          label: 'Subtotal',
          original: currency(originalOrder.subtotal_price),
          refunded: refundOrder ? currency(refundOrder.subtotal_price) : null,
        },
        {
          label: 'Shipping',
          original: `${originalShippingPrice} - ${originalShippingTitle}`,
          refunded: `${refundedShippingPrice} - ${refundedShippingTitle}`,
        },
        taxLines,
        {
          label: 'Total Tax',
          original: currency(originalOrder.total_tax),
          refunded: refundOrder ? currency(refundOrder.total_tax) : 0,
        },
        {
          label: 'Adjustments',
          original: adjustments ? currency(-adjustments) : '',
          refunded: '',
        },
        {
          label: 'Total',
          original: currency(parseFloat(originalOrder.total_price) - adjustments),
          refunded: refundOrder ? currency(refundOrder.total_price) : 0,
        },
      ].flat();
    },
  },
  methods: {
    areProductsEqual({row: {returnItem}}) {
      return !this.discountPriceRule && Number(returnItem.quantity) > 0
          ? 'highlight'
          : '';
    },
    arePaymentLinesEqual({row: {original, refunded}}) {
      if (original.price && refunded.price) {
        const price = original.price !== refunded.price;
        const rate = original.rate !== refunded.rate;
        const title = original.title !== refunded.title;
        return price || rate || title ? 'highlight' : '';
      }

      if (original !== refunded) {
        return 'highlight';
      }

      return '';
    },
    highlightChanges({row: {original, updated, refunded}}) {
      const rowClasses = [];

      if (!original && !updated && !refunded) {
        rowClasses.push('hidden');
      }

      if ((original !== updated) && (original !== refunded)) {
        rowClasses.push('highlight');
      }

      return rowClasses;
    },
    extractProperty,
  },
};
</script>

<style>
.el-table .highlight {
  background-color: #dff0d8;
}

.el-table .bold {
  font-weight: bold;
}
</style>
